// src/components/Platform/CommunicationsCenter/CommunicationsCenterStyle.js

import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PageContainer = styled.div`
  font-family: "Poppins", sans-serif;
  background: linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%);
  padding: 32px 100px;
  box-sizing: border-box;
  min-height: 100vh;
  animation: ${fadeIn} 0.5s ease-out;
`;

export const Header = styled.header`
  margin-bottom: 32px;
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  p {
    font-size: 1rem;
    color: #64748b;
    margin: 0;
  }
`;

export const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: flex-start;
`;

export const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 24px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

// --- Coluna da Biblioteca ---
export const LibrarySection = styled.section`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

export const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 12px;
  margin-bottom: 16px;

  svg {
    color: #94a3b8;
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    padding: 12px 8px;
    font-size: 0.9rem;
  }
`;

export const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

export const FilterButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.active ? "#3b82f6" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#475569")};

  &:hover {
    background-color: ${(props) => (props.active ? "#2563eb" : "#f8fafc")};
  }
`;

export const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 8px; /* Para a barra de rolagem não sobrepor o conteúdo */
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #94a3b8;
  font-weight: 500;
`;

// --- Coluna do Compositor ---
export const ComposerSection = styled.section`
  position: sticky;
  top: 24px;
`;

export const ComposerForm = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormField = styled.div``;

export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
`;

const commonInputStyles = css`
  width: 100%;
  padding: 12px 14px;
  font-size: 0.95rem;
  font-family: "Poppins", sans-serif;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background-color: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;

export const Input = styled.input`
  ${commonInputStyles}
`;
export const TextArea = styled.textarea`
  ${commonInputStyles}
`;
export const Select = styled.select`
  ${commonInputStyles}
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

export const ActionButton = styled.button`
  flex-grow: 1;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s, transform 0.1s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return css`
          background-color: #3b82f6;
          color: white;
          &:hover {
            background-color: #2563eb;
          }
        `;
      case "secondary":
        return css`
          background-color: #fff;
          color: #475569;
          border-color: #cbd5e1;
          &:hover {
            background-color: #f8fafc;
          }
        `;
      case "tertiary":
        return css`
          width: 100%;
          margin-top: 12px;
          background-color: #f1f5f9;
          color: #475569;
          &:hover {
            background-color: #e2e8f0;
          }
        `;
      default:
        return ``;
    }
  }}

  &:active {
    transform: scale(0.98);
  }
`;

export const PreviewArea = styled.div`
  margin-top: 24px;
`;

export const PreviewTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 16px;
`;

export const SearchAndSelectContainer = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 8px;
`;

export const ClientSearchInput = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-bottom: 1px solid #e2e8f0;

  input {
    width: 100%;
    border: none;
    outline: none;
    padding: 12px 8px;
    font-size: 0.9rem;
    background: transparent;
  }
`;

export const ResultsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 0;
  max-height: 150px;
  overflow-y: auto;
`;

export const ResultItem = styled.li`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: #f1f5f9;
  }
`;

export const SelectedClientsList = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ClientTag = styled.div`
  display: flex;
  align-items: center;
  background-color: #eef2ff;
  color: #4338ca;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;

  button {
    background: none;
    border: none;
    color: #6366f1;
    margin-left: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    line-height: 1;

    &:hover {
      color: #4338ca;
    }
  }
`;
